import React from "react";
import { useNavigate } from "react-router-dom";
import StampsTemplate from "./components/StampTemplate";
import { FaStar } from "react-icons/fa";
import { FiCalendar, FiClock, FiRefreshCw, FiTrendingUp } from "react-icons/fi";

export default function LoyaltyCard({ card, index }) {
  const navigate = useNavigate();

  const offset = index * 60;

  const handleClick = () => {
    navigate(`/loyaltywallets/${card._id}`);
  };

  const gradientCombos = [
    "bg-gradient-to-r from-red-500 to-blue-600",
    "bg-gradient-to-r from-yellow-400 to-red-500",
    "bg-gradient-to-r from-green-400 to-teal-500",
    "bg-gradient-to-r from-pink-500 to-purple-600",
    "bg-gradient-to-r from-indigo-400 to-sky-500",
    "bg-gradient-to-r from-orange-400 to-amber-500",
    "bg-gradient-to-r from-cyan-500 to-violet-600",
    "bg-gradient-to-r from-emerald-400 to-blue-600",
    "bg-gradient-to-r from-lime-400 to-green-600",
  ];

  const leftGradientOptions = [
    "#FF5F6D",
    "#FB7BA2",
    "#FF6B6B",
    "#FF9A8B",
    "#F5576C",
    "#FF85B3", // soft bubblegum pink
    "#FF3CAC", // hot pink

    "#A855F7", // purple-500
    "#D946EF", // fuchsia-500
    "#C084FC", // violet-400

    "#38BDF8", // sky-400
    "#60A5FA", // blue-400
    "#3B82F6", // blue-500
    "#0EA5E9", // sky-500

    "#4ADE80", // green-400
    "#22C55E", // green-500
    "#34D399", // emerald-400
  ];

  const randomLeftColor =
    leftGradientOptions[Math.floor(Math.random() * leftGradientOptions.length)];

  const DEFAULT_RIGHT_COLOR = "#38BDF8";

  const fallbackIndex =
    parseInt(card._id?.slice(-2), 16) % gradientCombos.length;
  const fallbackGradientClass = gradientCombos[fallbackIndex];

  const isCustomGradient = !!card.CardColor;
  const gradientStyle = isCustomGradient
    ? {
        backgroundImage: `linear-gradient(to top right, ${randomLeftColor}, ${
          card.CardColor || DEFAULT_RIGHT_COLOR
        })`,
      }
    : {};

  const colorIndex = card.CardColor
    ? parseInt(card.CardColor.replace("#", "").substring(0, 2), 16) %
      gradientCombos.length
    : 0;

  const gradientBg = gradientCombos[colorIndex];

  const getBonusIndexes = (stampsselected) => {
    if (stampsselected <= 5) return [stampsselected - 1];

    const numBonuses = stampsselected <= 10 ? 2 : stampsselected <= 15 ? 3 : 4;

    return Array.from(
      { length: numBonuses },
      (_, i) => Math.round(((i + 1) / numBonuses) * stampsselected) - 1
    );
  };

  const bonusIndexes = getBonusIndexes(card.stampsselected);
  const upcomingBonusIndex = bonusIndexes.find(
    (idx) => card.stampCount === idx
  );

  const timeAgoWithIcon = (dateString) => {
    const created = new Date(dateString);
    const now = new Date();
    const diffMs = now - created;

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years >= 1) {
      return {
        text: `${years} yr${years > 1 ? "s" : ""}`,
        Icon: FiCalendar,
      };
    }

    if (months >= 1) {
      return {
        text: `${months} mth${months > 1 ? "s" : ""} `,
        Icon: FiRefreshCw,
      };
    }

    if (days >= 1) {
      return {
        text: `${days} day${days > 1 ? "s" : ""} `,
        Icon: FiTrendingUp,
      };
    }

    if (hours >= 1) {
      return {
        text: `${hours} hr${hours > 1 ? "s" : ""} `,
        Icon: FiClock,
      };
    }

    if (minutes >= 1) {
      return {
        text: `${minutes} min${minutes > 1 ? "s" : ""} `,
        Icon: FiClock,
      };
    }

    return {
      text: "Now",
      Icon: FiClock,
    };
  };

  return (
    <div
      onClick={handleClick}
      className={`
        cursor-pointer
        transition-all duration-300
       rounded-xl
        shadow-lg ring-2 ring-black/10
        lg:static lg:top-auto lg:z-auto
        absolute left-0 right-0
      `}
      style={{
        top: `${offset}px`,
        zIndex: index + 1,
        marginBottom: "12px", // adds space for shadow between cards

        ...gradientStyle,
      }}
    >
      <div className="p-4 h-[250px] flex flex-col justify-between relative overflow-hidden rounded-xl">
        {/* Header */}
        <div className="flex justify-between items-center">
          {/* Left: Logo & Brand */}
          <div className="flex items-center gap-2">
            <img
              src={card.operatorPhoto}
              alt={card.operatorName}
              className="w-8 h-8 rounded-full object-cover border border-white shadow"
            />
            <h2
              className="
    font-semibold text-white leading-tight
    max-w-[150px] sm:max-w-[160px]
     line-clamp-2
    text-lg
  "
              style={{
                fontSize: card.operatorName.length > 20 ? "0.9rem" : "1.125rem",
              }}
            >
              {card.operatorName}
            </h2>
          </div>

          {/* Right: Stamps Info + Bonus Badge */}
          <div className="flex items-center gap-1">
            <div className="flex items-center gap-2 text-white text-sm">
              <span>
                {card.stampCount}/{card.stampsselected}
              </span>
              {upcomingBonusIndex === undefined && (
                <span className="flex items-center gap-1 text-sm text-white/70">
                  {(() => {
                    const { text, Icon } = timeAgoWithIcon(card.createdAt);
                    return (
                      <>
                        <Icon className="text-white/80" size={14} />
                        {text}
                      </>
                    );
                  })()}
                </span>
              )}{" "}
            </div>

            {/* Bonus badge only if one away */}
            {upcomingBonusIndex !== undefined && (
              <div className="bg-redlight text-text text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-md animate-pulse">
                <FaStar size={12} />
                Bonus {bonusIndexes.indexOf(upcomingBonusIndex) + 1}!
              </div>
            )}
          </div>
        </div>

        {/* Stamps */}
        <div className="p-3 flex justify-start items-start">
          <StampsTemplate
            stampsselected={card.stampsselected}
            stampCount={card.stampCount}
            pickImg={"/static/media/coffee.0072c842.PNG"}
            pickunImg={"/static/media/coffee.0072c842.PNG"}
            stampedBackground={"#FFF"}
            unstampedBackground={"#FFF"}
            bonusList={card.bonuses}
          />
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center text-white">
          <div className="text-[12px] tracking-widest">
            .... {card.writer?.slice(-4)}
          </div>{" "}
          <div className="text-[12px] tracking-widest">{card.type}</div>
        </div>
      </div>
    </div>
  );
}
